import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Search, UserPlus, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, StatusBadge, EmptyState } from "../../components/common";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, useFetchGate } from "../../components/FetchBar";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { MultiSelect } from "../../components/ui/multi-select";
import { splitIds, joinIds } from "../../components/TerritoryFilter";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, errMsg, fmtINR } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";
import { StaffLabel, staffLabelText } from "../../components/StaffAvatar";
import { AutoAssignProgressCard, useAutoAssignJob } from "../../components/AutoAssignProgress";
import { UnassignedWardsPanel } from "../../components/UnassignedWardsPanel";
import { CoverageAlertsCard } from "../../components/CoverageAlertsCard";

export default function AssignmentWorkspace() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [zones, setZones] = useState([]);
  const [wards, setWards] = useState([]);
  const [collectors, setCollectors] = useState([]);
  const [filters, setFilters] = useState({ search: "", zone_id: "", ward_id: "", assigned: "" });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [collectorId, setCollectorId] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [showLastRun, setShowLastRun] = useState(true);
  const [gapsRefreshKey, setGapsRefreshKey] = useState(0); // Phase 92: bump when a run finishes

  // Phase 133: the property list loads only on Fetch, with the filters applied at that moment
  const gate = useFetchGate(filters);
  const [loading, setLoading] = useState(false);
  useEffect(() => { setPage(1); }, [gate.appliedKey]);
  const load = useCallback(async () => {
    if (!gate.applied) return;
    const params = { page, limit: 50 };
    Object.entries(gate.applied).forEach(([k, v]) => { if (v) params[k] = v; });
    setLoading(true);
    try {
      const r = await api.get(`/t/${code}/properties`, { params });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, [code, gate.appliedKey, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const live = useLiveCollections(code);   // Phase 149: demand / balance / status columns follow the live ledger
  useEffect(() => { if (gate.fetched) load().catch(() => {}); }, [load, gate.fetched, gate.version, live]);
  useEffect(() => {
    api.get(`/t/${code}/masters/zones`).then((r) => setZones(r.data)).catch(() => {});
    api.get(`/t/${code}/masters/wards`, { params: { include_inactive: false } }).then((r) => setWards(r.data)).catch(() => {});
    api.get(`/t/${code}/collectors`).then((r) => setCollectors(r.data)).catch(() => {});
  }, [code]);
  // Phase 176: zone / ward filters are multi-select — comma-joined ids travel straight to the API
  const zoneSel = splitIds(filters.zone_id), wardSel = splitIds(filters.ward_id);
  const wardOptions = wards.filter((w) => !zoneSel.length || zoneSel.includes(w.zone_id));
  const zoneNameOf = (id) => zones.find((z) => z.id === id)?.zone_name;
  const changeZones = (ids) => {
    const allowed = new Set(wards.filter((w) => !ids.length || ids.includes(w.zone_id)).map((w) => w.id));
    setFilters({ ...filters, zone_id: joinIds(ids), ward_id: joinIds(wardSel.filter((id) => allowed.has(id))) });
  };

  const toggleAll = (checked) => setSelected(checked ? data.items.map((p) => p.id) : []);
  const toggleOne = (id, checked) => setSelected(checked ? [...selected, id] : selected.filter((x) => x !== id));

  const doAssign = async () => {
    if (!collectorId) return toast.error("Select a tax collector");
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/assignments/bulk`, { property_ids: selected, tax_collector_id: collectorId, notes });
      toast.success(`${r.data.created} assigned, ${r.data.reassigned} reassigned`);
      setAssignOpen(false);
      setSelected([]);
      setNotes("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  // Phase 91: auto-assign runs as a background job (batches of 500) with live progress
  const autoJob = useAutoAssignJob(code, {
    onFinished: (j) => {
      if (j.status === "completed") {
        if (j.assigned === 0 && j.no_collector === 0) toast.info("Nothing to do — every property is already assigned");
        else toast.success(`${j.assigned} propert${j.assigned === 1 ? "y" : "ies"} auto-assigned to ${(j.collectors || []).length} collector${(j.collectors || []).length === 1 ? "" : "s"} by territory` +
          (j.no_collector > 0 ? ` — ${j.no_collector} left unassigned (no collector covers their ward)` : ""));
      } else if (j.status === "cancelled") toast.info(`Stopped — ${j.assigned} propert${j.assigned === 1 ? "y was" : "ies were"} assigned before stopping`);
      else if (j.status === "failed") toast.error(j.error || "Auto-assign was interrupted");
      setShowLastRun(true);
      if (j.status !== "failed") setGapsRefreshKey((k) => k + 1); // reload the unassigned-wards snapshot
      load();
    },
  });
  const doAutoAssign = () => { setShowLastRun(true); autoJob.start(); };

  return (
    <div>
      <PageHeader title="Assignment Workspace" subtitle="Select properties and assign them to tax collectors">
        <div className="flex gap-2">
          <Button variant="outline" onClick={doAutoAssign} disabled={autoJob.starting || autoJob.running} data-testid="auto-assign-button">
            <Wand2 className="h-4 w-4 mr-2" /> {autoJob.running ? "Auto-assigning…" : autoJob.starting ? "Starting…" : "Auto-assign by territory"}
          </Button>
          <Button disabled={selected.length === 0} onClick={() => setAssignOpen(true)} data-testid="bulk-assign-button">
            <UserPlus className="h-4 w-4 mr-2" /> Assign {selected.length > 0 ? `(${selected.length})` : ""}
          </Button>
        </div>
      </PageHeader>

      {(autoJob.running || showLastRun) && (
        <AutoAssignProgressCard code={code} job={autoJob.job} onCancel={autoJob.cancel} cancelling={autoJob.cancelling}
          onDismiss={() => setShowLastRun(false)} />
      )}

      {/* Phase 156: wards whose properties have no collector who can see them today */}
      <CoverageAlertsCard code={code} compact refreshKey={gapsRefreshKey} hideWhenClear className="mb-4" />

      {/* Phase 92: wards whose properties had no covering collector (last run snapshot / live) */}
      <UnassignedWardsPanel code={code} refreshKey={gapsRefreshKey} running={autoJob.running} />

      <Card className="shadow-sm mb-4">
        <FetchForm onFetch={gate.fetch} testId="assign-filter-form">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search PIN, owner, address..." value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="assign-search-input" />
          </div>
          <MultiSelect options={zones.map((z) => ({ value: z.id, label: z.zone_name }))} value={zoneSel} onChange={changeZones}
            allLabel="All Zones" noun="zone" placeholder="Search zones…" className="w-40" testId="assign-zone-select" />
          <MultiSelect options={wardOptions.map((w) => ({ value: w.id, label: w.ward_name, group: zoneSel.length === 1 ? undefined : zoneNameOf(w.zone_id) }))}
            value={wardSel} onChange={(ids) => setFilters({ ...filters, ward_id: joinIds(ids) })}
            allLabel="All Wards" noun="ward" placeholder="Search wards…" className="w-40" testId="assign-ward-select" />
          <Select value={filters.assigned || "all"} onValueChange={(v) => setFilters({ ...filters, assigned: v === "all" ? "" : v })}>
            <SelectTrigger className="w-40" data-testid="assign-status-select"><SelectValue placeholder="Assignment" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Properties</SelectItem>
              <SelectItem value="no">Unassigned only</SelectItem>
              <SelectItem value="yes">Assigned only</SelectItem>
            </SelectContent>
          </Select>
          <FetchDirtyHint dirty={gate.dirty} testId="assign-fetch-dirty" />
          <FetchButton type="submit" size="sm" loading={loading} dirty={gate.dirty} fetched={gate.fetched} testId="assign-fetch-button" />
        </CardContent>
        </FetchForm>
      </Card>

      {!gate.fetched ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={gate.fetch} testId="assign-fetch-prompt"
            subtitle="Search or pick a zone, ward and assignment status above, then click Fetch to list the properties to assign." />
        </CardContent></Card>
      ) : !data ? <Spinner /> : data.items.length === 0 ? <EmptyState title="No properties match filters" subtitle="Adjust the filters and Fetch again" /> : (
        <Card className="shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table data-testid="assignment-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox checked={selected.length === data.items.length && data.items.length > 0} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
                    </TableHead>
                    <TableHead>PIN</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead className="hidden md:table-cell">Ward / Mohalla</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Assignment</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((p) => (
                    <TableRow key={p.id} className="hover:bg-muted/60">
                      <TableCell>
                        <Checkbox checked={selected.includes(p.id)} onCheckedChange={(c) => toggleOne(p.id, c)} data-testid={`select-property-${p.pin}`} />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{p.pin}</TableCell>
                      <TableCell className="text-sm font-medium">{p.owner_name}</TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{p.ward_label} • {p.mohalla_label || "-"}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{fmtINR(p.demand_summary.balance)}</TableCell>
                      <TableCell><StatusBadge status={p.payment_status} /></TableCell>
                      <TableCell>
                        {p.assignment ? (
                          <div>
                            <p className="text-xs font-medium"><StaffLabel code={code} userId={p.assignment.tax_collector_id} name={p.assignment.collector_name} /></p>
                            <StatusBadge status={p.assignment.status} />
                          </div>
                        ) : <span className="text-xs text-muted-foreground">Unassigned</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-xs text-muted-foreground">Page {page} • {data.total} total • {selected.length} selected</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page * 50 >= data.total} onClick={() => setPage(page + 1)}>Next</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Assign {selected.length} properties</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Tax collector</Label>
              <Select value={collectorId} onValueChange={setCollectorId}>
                <SelectTrigger data-testid="assign-collector-select"><SelectValue placeholder="Select collector" /></SelectTrigger>
                <SelectContent>
                  {collectors.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{staffLabelText(c.name, c.employee_id)} · {c.stats.assigned} assigned</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Notes (optional)</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <Button className="w-full" onClick={doAssign} disabled={busy} data-testid="assign-confirm-button">
              {busy ? "Assigning..." : "Confirm assignment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
